import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import set from 'lodash/set';
import clone from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import View from './AccountAddEditView';
import {
  save,
  updateValidation,
  fetchData,
  updateSupportData,
  bumpProgressBar,
} from '../../AdvWorkflow/AdvWorkflowActions';
import {
  setViewId,
  updateDraftAccount,
  saveDraftAccount,
  addDraftAccount,
  setDraftAccount,
} from './AccountsManualStepActions';
import { OVERVIEW } from './AccountsManualStepViews';
import { currentClientId, iterator } from '../../AdvWorkflow/AdvWorkflowSelector';
import {
  BACKEND_KEY,
  DOLLAR_CONTRIBUTION,
  PERCENTAGE_CONTRIBUTION,
} from '../FinancialAccountsWorkflowConstants';
import workflowAnalyticsEvents from '../../AnalyticsWorkflow/AnalyticsWorkflow';
import {
  groupsSelector,
  accountTypesOptionsSelector,
  accountOwnersOptionsSelector,
  accountOwnersGroupsSelector,
  accountDraftSelector,
  nullableAccountTypeOptionSelector,
  nullableAccountOwnerOptionSelector,
  interestRateLabelSelector,
  isMortgageAccountSelector,
  mortgageTypesOptionsSelector,
  nullableMortgageTypeOptionSelector,
  monthlyContributionAmountLabelSelector,
  contributionSelector,
  contributionTypeSelector,
  dollarContributionKeyNameSelector,
  percentageContributionKeyNameSelector,
  isEmployerSponsoredRetirementAccountSelector,
  isEmployerMatchableRetirementAccountSelector,
  canEmployeeMaxContributionSelector,
  contributionPeriodSelector,
  hasContributionPeriodOptionSelector,
  hasMaximizeContributionsSelector,
  hasContributionPercentOptionSelector,
  accountTypesSelector,
  isStudentLoanAccountSelector,
} from './AccountAddEditSelector';

import { accountsSelector, previousViewIdSelector } from './AccountsManualStepSelector';
import { onCancel } from './AccountAddEditActions';
import { mapEmployerMatchData } from '../../SharedComponents/EmployerMatch/EmployerMatchUtils';

const mapStateToProps = (state, ownProps) => ({
  clientId: currentClientId(state, ownProps),
  iterator: iterator(state, ownProps),
  account: accountDraftSelector(state, ownProps),

  graphAccounts: accountsSelector(state, ownProps),

  accountTypeOption: nullableAccountTypeOptionSelector(state, ownProps),
  accountOwnerOption: nullableAccountOwnerOptionSelector(state, ownProps),
  mortgageTypeOption: nullableMortgageTypeOptionSelector(state, ownProps),

  groups: groupsSelector(state, ownProps),
  accountOwnersGroups: accountOwnersGroupsSelector(state, ownProps),

  accountTypesOptions: accountTypesOptionsSelector(state, ownProps),
  accountOwnersOptions: accountOwnersOptionsSelector(state, ownProps),
  mortgageTypesOptions: mortgageTypesOptionsSelector(state, ownProps),
  dollarContributionKeyName: dollarContributionKeyNameSelector(state, ownProps),
  percentageContributionKeyName: percentageContributionKeyNameSelector(state, ownProps),

  interestRateLabel: interestRateLabelSelector(state, ownProps),

  isMortgageAccount: isMortgageAccountSelector(state, ownProps),
  isStudentLoanAccount: isStudentLoanAccountSelector(state, ownProps),
  monthlyContributionAmountLabel: monthlyContributionAmountLabelSelector(state, ownProps),

  contribution: contributionSelector(state, ownProps),
  contributionType: contributionTypeSelector(state, ownProps),
  previousViewId: previousViewIdSelector(state, ownProps),

  isEmployerSponsoredRetirementAccount: isEmployerSponsoredRetirementAccountSelector(
    state,
    ownProps,
  ),
  isEmployerMatchableRetirementAccount: isEmployerMatchableRetirementAccountSelector(
    state,
    ownProps,
  ),
  canEmployeeMaxContribution: canEmployeeMaxContributionSelector(state, ownProps),
  hasMaximizeContributions: hasMaximizeContributionsSelector(state, ownProps),
  contributionPeriod: contributionPeriodSelector(state, ownProps),
  hasContributionPeriodOption: hasContributionPeriodOptionSelector(state, ownProps),
  hasContributionPercentOption: hasContributionPercentOptionSelector(state, ownProps),
  accountTypes: accountTypesSelector(state, ownProps),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onSave: (account, graphAccounts, workflowId, clientId) => () => {
    let newAccount;
    let rollbackAccount;
    let addedNewAccount = !!account.frontEndId;
    if (!account.AccountId && !account.frontEndId) {
      newAccount = clone(account);
      newAccount.frontEndId = uuidv4();
      addedNewAccount = true;
      dispatch(addDraftAccount(newAccount, graphAccounts, workflowId, clientId));
    } else {
      rollbackAccount = graphAccounts.find(({ AccountId }) => AccountId === account.AccountId);
      dispatch(saveDraftAccount(account, graphAccounts, workflowId, clientId));
    }

    dispatch(
      save(
        BACKEND_KEY,
        () => {
          dispatch(fetchData(workflowId, clientId, [BACKEND_KEY]));
          dispatch(bumpProgressBar(workflowId));
          dispatch(setViewId(OVERVIEW, workflowId, clientId));
          dispatch(setDraftAccount(null, workflowId, clientId));
          dispatch(
            updateValidation(set({}, `${BACKEND_KEY}.AccountAddEdit`, []), workflowId, clientId),
          );
        },
        errors => {
          dispatch(
            updateValidation(
              set({}, `${BACKEND_KEY}.AccountAddEdit`, errors),
              workflowId,
              clientId,
            ),
          );
          if (newAccount) {
            dispatch(setDraftAccount(newAccount, workflowId, clientId));
          }

          if (rollbackAccount) {
            dispatch(saveDraftAccount(rollbackAccount, graphAccounts, workflowId, clientId));
          }
        },
        workflowId,
        clientId,
        false,
        null,
        accounts => {
          if (addedNewAccount) {
            workflowAnalyticsEvents(workflowId, 'manual_account_added');
          }
          dispatch(
            updateSupportData(
              {
                [BACKEND_KEY]: {
                  HasYourFamilyStartedSaving: accounts && accounts.length !== 0,
                },
              },
              workflowId,
              clientId,
            ),
          );
        },
      ),
    );
  },
  onCancel: (workflowId, clientId, account, graphAccounts, previousViewId) => () => {
    dispatch(onCancel(workflowId, clientId, account, graphAccounts, previousViewId));
  },
  accountInputChanged: (path, workflowId, clientId) => event => {
    dispatch(updateDraftAccount(set({}, path, event.target.value), workflowId, clientId));
  },
  accountDateChanged: (path, workflowId, clientId) => value => {
    dispatch(updateDraftAccount(set({}, path, value), workflowId, clientId));
  },
  accountSimpleSelectChanged: (path, workflowId, clientId) => value => {
    const extractedValue = set({}, path, (value || {}).value);
    dispatch(
      updateDraftAccount(isNil(extractedValue) ? null : extractedValue, workflowId, clientId),
    );
  },
  accountTypeChanged: (workflowId, clientId) => value => {
    if (value) {
      dispatch(updateDraftAccount(value.extra, workflowId, clientId));
    } else {
      dispatch(updateDraftAccount(set({}, 'AccountType', null), workflowId, clientId));
    }
  },
  contributionTypeChanged: (type, workflowId, clientId) => () => {
    const newContributionValues = {
      ContributionAmount: null,
      ContributionPercent: null,
      EmployerMatchAmount: null,
      EmployerMatchPercent: null,
    };
    if (type === PERCENTAGE_CONTRIBUTION) {
      newContributionValues.ContributionPercent = '0';
      newContributionValues.EmployerMatchPercent = '0';
    } else if (type === DOLLAR_CONTRIBUTION) {
      newContributionValues.ContributionAmount = '0';
      newContributionValues.EmployerMatchAmount = '0';
    }
    dispatch(updateDraftAccount(newContributionValues, workflowId, clientId));
  },

  mapEmployerMatch: (account, workflowId, clientId) => {
    const mappedData = mapEmployerMatchData(account);
    if (mappedData) {
      const newData = { EmployerMultiTierMatch: mappedData };
      dispatch(updateDraftAccount(newData, workflowId, clientId));
    }
  },
  updateDraftAccount(updates) {
    dispatch(updateDraftAccount(updates, ownProps.workflowId, ownProps.clientId));
  },
});

const AccountAddEditContainer = connect(mapStateToProps, mapDispatchToProps)(View);

AccountAddEditContainer.propTypes = {
  branch: PropTypes.arrayOf(PropTypes.number),
  workflowId: PropTypes.string.isRequired,
};

export default AccountAddEditContainer;
